import { memo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  ChevronDown,
  File,
  Copy,
  Check,
  PanelRightClose,
  PanelRightOpen,
  ChevronsDownUp,
  ChevronsUpDown,
  Maximize2,
  Minimize2,
  ExternalLink,
} from 'lucide-react';
import type { RenderResult } from '../types';
import toast from 'react-hot-toast';
import clsx from 'clsx';

interface OutputPanelProps {
  className?: string;
  isCollapsible?: boolean;
  isOutputCollapsed: boolean;
  setIsOutputCollapsed: (collapsed: boolean) => void;
  isRendering: boolean;
  renderResult: RenderResult | null;
  releaseName: string;
  namespace: string;
  onReleaseNameChange: (name: string) => void;
  onNamespaceChange: (namespace: string) => void;
  onOpenManifest: (name: string, content: string) => void;
}

function OutputPanelComponent({
  className,
  isCollapsible = true,
  isOutputCollapsed,
  setIsOutputCollapsed,
  isRendering,
  renderResult,
  releaseName,
  namespace,
  onReleaseNameChange,
  onNamespaceChange,
  onOpenManifest,
}: OutputPanelProps) {
  const [copiedManifest, setCopiedManifest] = useState<string | null>(null);
  const [collapsedManifests, setCollapsedManifests] = useState<Set<string>>(new Set());
  const [expandedManifests, setExpandedManifests] = useState<Set<string>>(new Set());

  const handleCopyManifest = async (name: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedManifest(name);
      setTimeout(() => setCopiedManifest(null), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCopyAllManifests = async () => {
    if (!renderResult?.manifests) return;
    const allContent = Object.entries(renderResult.manifests || {})
      .map(([name, content]) => `---\n# Source: ${name}\n${content}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(allContent);
      setCopiedManifest('all');
      setTimeout(() => setCopiedManifest(null), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const toggleManifestCollapse = (name: string) => {
    setCollapsedManifests((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const collapseAllManifests = () => {
    if (!renderResult?.manifests) return;
    setCollapsedManifests(new Set(Object.keys(renderResult.manifests || {})));
  };

  const expandAllManifests = () => {
    setCollapsedManifests(new Set());
  };

  const toggleManifestExpanded = (name: string) => {
    setExpandedManifests((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  return (
    <div className={clsx("h-full bg-dark-850 flex flex-col", className)}>
      {isCollapsible && isOutputCollapsed ? (
        /* Collapsed State - Desktop only */
        <div className="h-full flex flex-col items-center py-2">
          <button
            onClick={() => setIsOutputCollapsed(false)}
            className="p-1.5 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded transition-colors"
            title="Expand output panel"
          >
            <PanelRightOpen className="w-4 h-4" />
          </button>
          <div className="flex-1 flex items-center justify-center">
            <span
              className="text-[10px] text-dark-500 uppercase tracking-wider whitespace-nowrap"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              Output
            </span>
          </div>
          {/* Status indicator when collapsed */}
          <div className="mt-auto pb-2">
            {isRendering ? (
              <Clock className="w-3 h-3 text-accent-warning animate-pulse" />
            ) : renderResult?.errors && renderResult.errors.length > 0 ? (
              <AlertCircle className="w-3 h-3 text-accent-error" />
            ) : renderResult && Object.keys(renderResult.manifests || {}).length > 0 ? (
              <CheckCircle className="w-3 h-3 text-accent-success" />
            ) : null}
          </div>
        </div>
      ) : (
        /* Expanded State */
        <>
          {/* Header */}
          <div className="p-3 md:p-2 border-b border-dark-700">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs md:text-[10px] font-semibold text-dark-400 uppercase tracking-wider">
                Rendered Output
              </h2>
              <div className="flex items-center gap-1">
                {renderResult && Object.keys(renderResult.manifests || {}).length > 0 && (
                  <>
                    <button
                      onClick={collapsedManifests.size === Object.keys(renderResult.manifests || {}).length ? expandAllManifests : collapseAllManifests}
                      className="p-1.5 md:p-1 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded transition-colors"
                      title={collapsedManifests.size === Object.keys(renderResult.manifests || {}).length ? "Expand all manifests" : "Collapse all manifests"}
                    >
                      {collapsedManifests.size === Object.keys(renderResult.manifests || {}).length ? (
                        <ChevronsUpDown className="w-4 h-4 md:w-3 md:h-3" />
                      ) : (
                        <ChevronsDownUp className="w-4 h-4 md:w-3 md:h-3" />
                      )}
                    </button>
                    <button
                      onClick={handleCopyAllManifests}
                      className="flex items-center gap-1 px-2 py-1 md:px-1.5 md:py-0.5 text-xs md:text-[10px] text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded transition-colors"
                      title="Copy all manifests"
                    >
                      {copiedManifest === 'all' ? (
                        <>
                          <Check className="w-3 h-3 md:w-2.5 md:h-2.5 text-accent-success" />
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3 md:w-2.5 md:h-2.5" />
                          <span>Copy All</span>
                        </>
                      )}
                    </button>
                  </>
                )}
                {isCollapsible && (
                  <button
                    onClick={() => setIsOutputCollapsed(true)}
                    className="p-1.5 md:p-1 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded transition-colors hidden md:block"
                    title="Collapse output panel"
                  >
                    <PanelRightClose className="w-4 h-4 md:w-3.5 md:h-3.5" />
                  </button>
                )}
              </div>
            </div>
            {/* Release Name and Namespace inputs */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] md:text-[9px] text-dark-500 uppercase tracking-wider block mb-0.5">
                  Release Name
                </label>
                <input
                  type="text"
                  value={releaseName}
                  onChange={(e) => onReleaseNameChange(e.target.value)}
                  className="w-full px-2 py-1.5 md:px-1.5 md:py-1 text-sm md:text-[11px] bg-dark-800 border border-dark-600 rounded text-dark-200 focus:outline-none focus:border-accent-primary"
                  placeholder="release-name"
                />
              </div>
              <div>
                <label className="text-[10px] md:text-[9px] text-dark-500 uppercase tracking-wider block mb-0.5">
                  Namespace
                </label>
                <input
                  type="text"
                  value={namespace}
                  onChange={(e) => onNamespaceChange(e.target.value)}
                  className="w-full px-2 py-1.5 md:px-1.5 md:py-1 text-sm md:text-[11px] bg-dark-800 border border-dark-600 rounded text-dark-200 focus:outline-none focus:border-accent-primary"
                  placeholder="default"
                />
              </div>
            </div>
          </div>

          {/* Output Content */}
          <div className="flex-1 overflow-y-auto scrollbar-hide-mobile">
            {renderResult?.errors && renderResult.errors.length > 0 ? (
              <div className="p-3 md:p-2">
                <div className="text-sm md:text-[11px] text-accent-error mb-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4 md:w-3 md:h-3" />
                  Render failed with {renderResult.errors.length} error(s)
                </div>
                <div className="space-y-2 md:space-y-1">
                  {renderResult.errors.map((error, index) => (
                    <div
                      key={index}
                      className="text-xs md:text-[10px] text-dark-300 bg-dark-800 p-2 md:p-1.5 rounded border border-dark-700"
                    >
                      {error.file && (
                        <div className="text-accent-error font-medium mb-0.5">{error.file}</div>
                      )}
                      <div className="text-dark-400">{error.message}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : renderResult && Object.keys(renderResult.manifests || {}).length > 0 ? (
              <div className="p-2 md:p-1.5 space-y-2 md:space-y-1.5">
                {Object.entries(renderResult.manifests || {}).map(([name, content]) => {
                  const isCollapsed = collapsedManifests.has(name);
                  const isExpanded = expandedManifests.has(name);
                  return (
                    <div key={name} className="bg-dark-800 rounded border border-dark-700">
                      <div
                        className={clsx(
                          "flex items-center justify-between px-3 py-2 md:px-2 md:py-1 cursor-pointer hover:bg-dark-750",
                          !isCollapsed && "border-b border-dark-700"
                        )}
                        onClick={() => toggleManifestCollapse(name)}
                      >
                        <div className="flex items-center gap-2 md:gap-1.5">
                          {isCollapsed ? (
                            <ChevronRight className="w-4 h-4 md:w-3 md:h-3 text-dark-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 md:w-3 md:h-3 text-dark-500" />
                          )}
                          <File className="w-4 h-4 md:w-3 md:h-3 text-green-500" />
                          <span className="text-sm md:text-[11px] text-dark-200 font-medium truncate max-w-[200px] md:max-w-[150px]">
                            {name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 md:gap-0.5">
                          {!isCollapsed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleManifestExpanded(name);
                              }}
                              className="p-1.5 md:p-0.5 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded transition-colors"
                              title={isExpanded ? "Collapse view" : "Expand view"}
                            >
                              {isExpanded ? (
                                <Minimize2 className="w-4 h-4 md:w-3 md:h-3" />
                              ) : (
                                <Maximize2 className="w-4 h-4 md:w-3 md:h-3" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenManifest(name, content);
                            }}
                            className="p-1.5 md:p-0.5 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded transition-colors"
                            title="Open in editor"
                          >
                            <ExternalLink className="w-4 h-4 md:w-3 md:h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyManifest(name, content);
                            }}
                            className="p-1.5 md:p-0.5 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded transition-colors"
                            title="Copy manifest"
                          >
                            {copiedManifest === name ? (
                              <Check className="w-4 h-4 md:w-3 md:h-3 text-accent-success" />
                            ) : (
                              <Copy className="w-4 h-4 md:w-3 md:h-3" />
                            )}
                          </button>
                        </div>
                      </div>
                      {!isCollapsed && (
                        <pre
                          className={clsx(
                            "p-3 md:p-2 text-xs md:text-[10px] text-dark-300 overflow-x-auto font-mono leading-relaxed overflow-y-auto",
                            isExpanded ? "max-h-[60vh] md:max-h-[70vh]" : "max-h-64 md:max-h-48"
                          )}
                        >
                          {content}
                        </pre>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm md:text-[11px] text-dark-500">No output yet</p>
              </div>
            )}
          </div>

          {/* Output footer with stats */}
          {renderResult && Object.keys(renderResult.manifests || {}).length > 0 && (
            <div className="border-t border-dark-700 p-2 md:p-1.5">
              <div className="flex items-center justify-between text-xs md:text-[10px] text-dark-400">
                <span>{Object.keys(renderResult.manifests || {}).length} manifest(s)</span>
                <span>{renderResult.renderTimeMs}ms</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Memoize to prevent re-renders when parent state changes
export const OutputPanel = memo(OutputPanelComponent);
